import { gql } from '@apollo/client';
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  FormControl,
  FormLabel,
} from '@apollo/orbit';
import { Select } from '@apollo/space-kit/Select';
import React from 'react';
import { SelectChangeEvent } from 'typings/events/selection-change-event';

import { InsightsLineChartTooltip } from 'src/app/graph/insights/InsightsLineChartTooltip';
import { Heatmap } from 'src/components/charts/Heatmap';
import {
  Histogram,
  usePrepareHistogramData,
} from 'src/components/charts/Histogram';
import { LineChart } from 'src/components/charts/LineChart';
import { Loading } from 'src/components/common/loading/Loading';
import { DateRangeBadge } from 'src/components/dateRangeBadge/DateRangeBadge';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { format } from 'src/lib/format';
import {
  FetchLatencyOverTimeResultFragment,
  Resolution,
} from 'src/lib/graphqlTypes/types';

import { InsightsHistogramTooltip } from '../../InsightsHistogramTooltip';

import { useFetchHeatmapStackInfo } from './useFetchHeatmapChartStacks';
import { useFetchHistogramStackInfo } from './useFetchHistogramStacks';
import {
  FetchLatencyMetricType,
  useFetchLatencyChartStacks,
} from './useFetchLatencyChartStacks';

export const fetchLatencyOverTimeResultFragment = gql`
  fragment FetchLatencyOverTimeResultFragment on SubgraphInsightsTimeseriesResult {
    roundedUpTo
    roundedDownFrom
    records {
      timestamp
      data {
        fetchLatencyHistogram {
          p50: durationMs(percentile: 0.5)
          p90: durationMs(percentile: 0.9)
          p95: durationMs(percentile: 0.95)
          p99: durationMs(percentile: 0.99)
          sparseBuckets
        }
      }
    }
  }
`;

type FetchLatencyChartType = 'histogram' | 'heatmap' | FetchLatencyMetricType;

function getChartDescription(metricType: FetchLatencyChartType) {
  switch (metricType) {
    case 'p50':
      return 'The 50th percentile of fetch latency.';
    case 'p90':
      return 'The 90th percentile of fetch latency.';
    case 'p95':
      return 'The 95th percentile of fetch latency.';
    case 'p99':
      return 'The 99th percentile of fetch latency.';
    case 'histogram':
      return 'The distribution of fetch latency.';
    case 'heatmap':
      return 'The heatmap showing fetch latency changes over time.';
    default:
      assertUnreachable(metricType);
  }
}

function getChartTitle(metricType: FetchLatencyChartType) {
  switch (metricType) {
    case 'p50':
      return 'P50 Fetch Latency';
    case 'p90':
      return 'P90 Fetch Latency';
    case 'p95':
      return 'P95 Fetch Latency';
    case 'p99':
      return 'P99 Fetch Latency';
    case 'histogram':
      return 'Fetch Latency Distribution';
    case 'heatmap':
      return 'Fetch Latency Heatmap';
    default:
      assertUnreachable(metricType);
  }
}

interface FetchLatencyOverTimeHeadingProps {
  metricType: FetchLatencyChartType;
  onFetchMetricTypeChange: (
    event: SelectChangeEvent<FetchLatencyChartType>,
  ) => void;
}

const FetchLatencyOverTimeHeading = ({
  metricType,
  onFetchMetricTypeChange,
}: FetchLatencyOverTimeHeadingProps) => {
  return (
    <div className="flex items-center gap-2">
      <FormControl>
        <FormLabel className="sr-only">Fetch Metric Type</FormLabel>
        <Select
          value={metricType}
          onChange={onFetchMetricTypeChange}
          feel="flat"
          className="self-center font-heading text-xl font-medium text-heading"
          placement="bottom-start"
        >
          <option className="font-normal" value="heatmap">
            Fetch latency heatmap
          </option>
          <option className="font-normal" value="histogram">
            Fetch latency distribution
          </option>
          <option className="font-normal" value="p50">
            P50 fetch latency
          </option>
          <option className="font-normal" value="p90">
            P90 fetch latency
          </option>
          <option className="font-normal" value="p95">
            P95 fetch latency
          </option>
          <option className="font-normal" value="p99">
            P99 fetch latency
          </option>
        </Select>
      </FormControl>
      <DateRangeBadge />
    </div>
  );
};

const PercentileChart = ({
  result,
  metricType,
  resolution,
}: {
  result?: FetchLatencyOverTimeResultFragment;
  metricType: FetchLatencyMetricType;
  resolution: Resolution;
}) => {
  const { fetchErrorsChartStackInfo } = useFetchLatencyChartStacks({
    result,
    metricType,
    resolution,
  });
  const formatValue = (_: 'major' | 'minor', value: number): string =>
    format.ms(value, 1, false);

  return (
    <LineChart
      className="h-56"
      stacked
      stacks={fetchErrorsChartStackInfo.stacks}
      resolution={resolution}
      allowSingleXAxisValue
      data-testid="subgraph-fetches-line-chart"
      formatValue={formatValue}
      tooltipLabelAs={
        <InsightsLineChartTooltip title={getChartTitle(metricType)} />
      }
    />
  );
};

const HistogramChart = ({
  result,
}: {
  result?: FetchLatencyOverTimeResultFragment;
}) => {
  const histogramStackInfo = useFetchHistogramStackInfo(result);
  const histogramData = usePrepareHistogramData({
    ...histogramStackInfo,
  });
  return (
    <Histogram
      data={histogramData}
      className="h-56"
      {...histogramStackInfo}
      data-analytics-category="Subgraph Insights"
      data-analytics-label="Latency histogram click"
      tooltipLabelAs={<InsightsHistogramTooltip title="Latency distribution" />}
      hideCursorArrow
    />
  );
};

const HeatmapChart = ({
  result,
  resolution,
}: {
  result?: FetchLatencyOverTimeResultFragment;
  resolution: Resolution;
}) => {
  const heatmapStackInfo = useFetchHeatmapStackInfo(resolution, result);
  return (
    <Heatmap className="h-56" {...heatmapStackInfo} resolution={resolution} />
  );
};

interface Props {
  className?: string;
  loading: boolean;
  result?: FetchLatencyOverTimeResultFragment;
  resolution: Resolution;
}

export function FetchLatencyOverTime({
  className,
  loading,
  result,
  resolution,
}: Props) {
  const [fetchChartType, setFetchChartType] =
    React.useState<FetchLatencyChartType>('heatmap');

  const onFetchMetricTypeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setFetchChartType(event.target.value as FetchLatencyChartType);
  };

  return (
    <Card variant="unstyled" className={className}>
      <CardHeader>
        <CardTitle>
          <div className="flex items-center">
            <FetchLatencyOverTimeHeading
              metricType={fetchChartType}
              onFetchMetricTypeChange={onFetchMetricTypeChange}
            />
          </div>
        </CardTitle>
        <CardDescription className="pl-3">
          {getChartDescription(fetchChartType)}
        </CardDescription>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="flex justify-center py-10 text-center">
            <Loading />
          </div>
        ) : fetchChartType === 'histogram' ? (
          <HistogramChart result={result} />
        ) : fetchChartType === 'heatmap' ? (
          <HeatmapChart result={result} resolution={resolution} />
        ) : (
          <PercentileChart
            result={result}
            metricType={fetchChartType}
            resolution={resolution}
          />
        )}
      </CardBody>
    </Card>
  );
}
